import { getErrorCode, isTerminalAuthError, NO_CURRENT_USER_CODE, readExpiryMs, withTimeout } from "@/lib/auth-token";
import { handleSignOut, isLeavingForLogin } from "@/lib/auth-utils";
import { auth, authReady } from "@/lib/firebase";
import { captureAuthEvent, captureAuthException } from "@/lib/sentry";

// ── Token retrieval ─────────────────────────────────────────────────────────

const REFRESH_TIMEOUT_MS = 15_000;

// Dev-only diagnostic logging for token age / refresh attempts. Stripped from
// production by Vite's dead-code elimination on `import.meta.env.DEV`.
function debugLog(...args: unknown[]): void {
  if (import.meta.env.DEV) {
    console.log("[auth]", ...args);
  }
}

function tokenAge(token: string): string {
  const exp = readExpiryMs(token);
  if (exp === null) return "exp=?";
  const remainingSec = Math.round((exp - Date.now()) / 1000);
  return `expires in ${remainingSec}s`;
}

let tokenPromise: Promise<string> | null = null;
let refreshPromise: Promise<string> | null = null;
let reportedRejectedAfterRefresh = false;

/**
 * Single-flighted token read. Awaits Firebase rehydration, then returns the
 * SDK's ID token. `getIdToken()` renews it on demand within 30s of expiry
 * (timed from issue, so device clock skew cannot trip it), which also covers
 * the first request after a laptop wakes.
 */
export async function getToken(): Promise<string> {
  if (typeof window === "undefined") return "";

  // E2E test override — development only, stripped from production bundles
  // because import.meta.env.DEV is inlined as `false` at build time.
  if (import.meta.env.DEV) {
    const testToken = window.__E2E_TOKEN;
    if (testToken) return testToken;
  }

  if (tokenPromise) return tokenPromise;

  tokenPromise = (async () => {
    try {
      await authReady;
      return auth.currentUser ? await auth.currentUser.getIdToken() : "";
    } finally {
      tokenPromise = null;
    }
  })();

  return tokenPromise;
}

// ── 401 recovery ────────────────────────────────────────────────────────────

async function forceRefreshToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      // Critical: wait for Firebase to finish rehydrating persisted state
      // before declaring "no user". Without this, the first request after
      // tab-resume / laptop-wake races the SDK and sees `currentUser === null`
      // for a few hundred ms, which previously triggered an immediate sign-out
      // even though the refresh token in IndexedDB was perfectly valid.
      await authReady;
      const user = auth.currentUser;
      if (!user) {
        const err = new Error(NO_CURRENT_USER_CODE);
        (err as { code?: string }).code = NO_CURRENT_USER_CODE;
        throw err;
      }
      debugLog("forceRefreshToken: requesting fresh token");
      const fresh = await withTimeout(user.getIdToken(true), REFRESH_TIMEOUT_MS, "token refresh");
      debugLog("forceRefreshToken: got fresh token,", tokenAge(fresh));
      return fresh;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Several requests can find the session over at once. handleSignOut marks the
// tab as leaving before it awaits, so only the first one signs out.
async function safeSignOut(reason: "refresh-failed") {
  if (isLeavingForLogin()) return;
  try {
    await handleSignOut(reason);
  } catch (err) {
    captureAuthException(err, { source: "safeSignOut" });
  }
}

// ── 401 interceptor fetch wrapper ───────────────────────────────────────────

const RETRY_BACKOFF_MS = 800;

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function attemptRefreshAndRetry(input: RequestInfo | URL, init: RequestInit | undefined): Promise<Response> {
  const freshToken = await forceRefreshToken();
  const retryHeaders = new Headers(init?.headers);
  retryHeaders.set("Authorization", `Bearer ${freshToken}`);
  return fetch(input, { ...init, headers: retryHeaders });
}

/**
 * Best-effort peek at `{error: {code}}` in a JSON error response. Returns
 * null if the body isn't JSON or doesn't have the expected shape. Clones
 * the response first so the original body stream is still readable by the
 * caller.
 */
async function peekErrorCode(response: Response): Promise<string | null> {
  try {
    const body = (await response.clone().json()) as {
      error?: { code?: string };
    };
    return body?.error?.code ?? null;
  } catch {
    return null;
  }
}

/**
 * Retry the request after stripping `X-Merchant-Id`. Used when the backend
 * rejects the stale active-merchant-id (e.g. the merchant was deleted while
 * the pointer still lived in storage). The caller is responsible for
 * clearing the active id before invoking this so future requests don't
 * carry it again.
 */
async function retryWithoutMerchantHeader(input: RequestInfo | URL, init: RequestInit | undefined): Promise<Response> {
  const retryHeaders = new Headers(init?.headers);
  retryHeaders.delete("X-Merchant-Id");
  return fetch(input, { ...init, headers: retryHeaders });
}

// Fetch-compatible signature without the static `preconnect` member. The
// backend's elysia types (aliased in for Eden) leak Bun's `fetch` typing into
// this program, which would otherwise require `preconnect` on every fetch-typed
// value; nothing here calls it, and eden.ts re-attaches it for Eden's fetcher.
export const interceptedFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const response = await fetch(input, init);

  // Stale-merchant recovery — runs BEFORE the 401 path. The auth plugin
  // returns 403 NO_MEMBERSHIP_FOR_MERCHANT when `X-Merchant-Id` points at a
  // membership that doesn't match (deleted membership, deleted merchant,
  // user signed in on a different account). Without this guard the user is
  // bricked on every page load until they manually clear storage. We clear
  // both sessionStorage + localStorage so the bad pointer doesn't come back
  // on reload, then retry once with no header.
  if (response.status === 403) {
    const code = await peekErrorCode(response);
    if (code === "NO_MEMBERSHIP_FOR_MERCHANT") {
      debugLog("403 NO_MEMBERSHIP_FOR_MERCHANT — clearing stale merchant id + retrying");
      setActiveMerchantId(null, { persist: true });
      return retryWithoutMerchantHeader(input, init);
    }
  }

  if (response.status !== 401) return response;

  // The refresh→sign-out cascade should only fire for real Firebase-auth
  // failures. The backend's auth plugin tags those with `AUTH_MISSING` (no
  // Bearer header reached the server) or `AUTH_INVALID` (expired/revoked
  // token). Any other 401 is a downstream business error — e.g. LINE's
  // Messaging API rejecting the channel access token on /v1/line/connect
  // surfaces as `LINE_TOKEN_INVALID` 401, and signing the user out there
  // would brick the connect flow on every bad-token submission.
  const code = await peekErrorCode(response);
  if (code && code !== "AUTH_MISSING" && code !== "AUTH_INVALID") {
    debugLog("401 with non-auth code, passing through:", code);
    return response;
  }

  debugLog("401 received, attempting refresh + retry");
  // First refresh + retry attempt.
  let firstResponse: Response;
  try {
    firstResponse = await attemptRefreshAndRetry(input, init);
  } catch (err) {
    const code = getErrorCode(err);
    // Refresh failed. Three outcomes:
    //   1. No user is signed in (anonymous visitor or signed out elsewhere) →
    //      surface the original 401 to the caller; do NOT sign out — there's
    //      nothing to sign out from, and forcing /login here would break
    //      public pages that happen to fire an authedFetch.
    //   2. Firebase signaled the session is dead → sign the user out.
    //   3. Anything else (network, timeout, transient IdP error) → return the
    //      original 401 to the caller and DO NOT sign out. The user can retry
    //      when the network recovers.
    if (code === NO_CURRENT_USER_CODE) {
      return response;
    }
    if (isTerminalAuthError(err)) {
      captureAuthEvent("refresh-failed-terminal", { code });
      await safeSignOut("refresh-failed");
    } else {
      captureAuthException(err, { source: "interceptedFetch.refresh" });
    }
    return response;
  }

  if (firstResponse.status !== 401) {
    debugLog("first retry succeeded");
    return firstResponse;
  }

  debugLog("first retry still 401, attempting second refresh after backoff");
  // One more attempt after a backoff: a transient server-side failure (the
  // backend fetching Google's signing keys) can reject one fresh token.
  await sleep(RETRY_BACKOFF_MS);
  let secondResponse: Response;
  try {
    secondResponse = await attemptRefreshAndRetry(input, init);
  } catch (err) {
    if (isTerminalAuthError(err)) {
      captureAuthEvent("refresh-failed-terminal", { code: getErrorCode(err) });
      await safeSignOut("refresh-failed");
    } else {
      captureAuthException(err, { source: "interceptedFetch.refresh.retry" });
    }
    return firstResponse;
  }

  // Still 401 with a token Firebase just issued: the session is alive and the
  // backend is at fault, so signing out would not help. Firebase alone decides
  // when a session is over (the terminal refresh errors above). During such a
  // fault every request lands here, so report it once per page load.
  if (secondResponse.status === 401 && !reportedRejectedAfterRefresh) {
    reportedRejectedAfterRefresh = true;
    captureAuthEvent("rejected-after-refresh");
  }
  return secondResponse;
};

/**
 * `interceptedFetch` with the static `preconnect` member re-attached so it
 * satisfies the `typeof fetch` shape Eden's `fetcher` slot requires. The
 * Bun-flavoured `fetch` type (leaked in via the aliased backend elysia types)
 * demands a static `preconnect`; nothing actually calls it. This WRAPS
 * `interceptedFetch` in a new function rather than mutating it, so the base
 * export keeps its plain fetch-like shape.
 */
export const interceptedFetchTyped: typeof fetch = Object.assign(
  (input: RequestInfo | URL, init?: RequestInit) => interceptedFetch(input, init),
  { preconnect: globalThis.fetch?.preconnect ?? (() => {}) },
);

/**
 * Ad-hoc authenticated fetch for the few callers that don't go through the
 * Eden client (e.g. OAuth auth-url endpoints that return a redirect URL).
 * Reuses the 401 retry path and the E2E token bypass; omits the Authorization
 * header when unauthenticated rather than sending `Bearer ` (empty).
 *
 * Sets `X-Merchant-Id` the same way Eden's header resolver does. Without it,
 * every permission-gated endpoint returns 403 NO_MERCHANT regardless of how
 * the user signed in.
 */
export async function authedFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (activeMerchantId) headers.set("X-Merchant-Id", activeMerchantId);
  return interceptedFetch(input, { ...init, headers });
}

// ── Active merchant header ─────────────────────────────────────────────────
//
// The backend's auth plugin requires `X-Merchant-Id` to resolve which
// merchant a request acts on (a user can be a member of N merchants).
// Without it, every permission-gated endpoint returns 403 NO_MERCHANT.
//
// Two persistence tiers:
//
// - `sessionStorage` (active id): the operating session's current pointer.
//   Cleared on sign-out so a different user on the same device doesn't
//   inherit it.
//
// - `localStorage` (preferred id): the user's explicit "I want to act as
//   merchant X" choice from the switcher. Survives browser close. Read at
//   bootstrap and validated against the user's current memberships before
//   use (so a removed-from-merchant pointer falls through).

const MERCHANT_ID_KEY = "auth:active-merchant-id";
const PREFERRED_MERCHANT_ID_KEY = "auth:preferred-merchant-id";

let activeMerchantId: string | null = (() => {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(MERCHANT_ID_KEY);
  } catch {
    return null;
  }
})();

export interface SetActiveMerchantOptions {
  /** Also write the value to localStorage so it survives browser close.
   *  Use when the user has explicitly chosen this merchant (switcher). */
  persist?: boolean;
}

export function setActiveMerchantId(id: string | null, opts: SetActiveMerchantOptions = {}): void {
  activeMerchantId = id;

  debugLog("[merchant-id] set:", id, "persist:", !!opts.persist);
  if (typeof window === "undefined") return;
  try {
    if (id) window.sessionStorage.setItem(MERCHANT_ID_KEY, id);
    else window.sessionStorage.removeItem(MERCHANT_ID_KEY);
    if (opts.persist) {
      if (id) window.localStorage.setItem(PREFERRED_MERCHANT_ID_KEY, id);
      else window.localStorage.removeItem(PREFERRED_MERCHANT_ID_KEY);
    }
  } catch {
    // Storage may throw in private mode / quota — header still works in
    // memory for the current tab, so swallow.
  }
}

export function getActiveMerchantId(): string | null {
  return activeMerchantId;
}

export function getPreferredMerchantId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(PREFERRED_MERCHANT_ID_KEY);
  } catch {
    return null;
  }
}

export function clearPreferredMerchantId(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PREFERRED_MERCHANT_ID_KEY);
  } catch {
    /* swallow */
  }
}
